import { useEffect, useRef, useState, type ElementRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ImageOff } from 'lucide-react'
import {
  MEDIA_ERROR_TITLE,
  thumbnailMediaUrl,
  useAuthorizedMediaUrl,
} from '../../shared/media/authorizedMedia'
import { useNearViewport } from '../../shared/media/useNearViewport'

export const IMAGE_PREVIEW_THUMB_SIZE = 'h-11 w-8'

/**
 * Ширина картинки для окна увеличения. Окно 300x400, портрет вписывается в
 * ~277 CSS-пикселей, на экране с dpr 1.75 это 486 физических — 480 закрывает их
 * без растяжения. Ширина штатная: лежит в дисковом кэше сервиса и её же просит
 * боковое превью карточки, так что для просмотренных макетов файл уже готов.
 */
const ZOOM_MEDIA_WIDTH = 480

interface ImagePreviewThumbProps {
  src: string
  alt?: string
  size?: string
  /** Ширина миниатюры на сервере; окно увеличения догружает свою, пошире. */
  thumbnailWidth?: number
  /**
   * Грузить, только когда строка подъехала к экрану. По умолчанию включено:
   * списки в этом приложении почти никогда не виртуализированы, и без
   * отложенной загрузки в разметку попадают все строки разом — приёмка тянет
   * порцию в 500 макетов, и столько же запросов уходит в очередь на
   * монтировании, верхний экран заполняется наравне с тем, до чего оператор не
   * долистает.
   *
   * Выключать (`lazy={false}`) стоит там, где картинка одна и заведомо в кадре
   * сразу при открытии: одиночное крупное превью, центральный кадр модалки,
   * лайтбокс. В виртуализированных таблицах строки и так появляются по мере
   * прокрутки, там проп можно не трогать — вреда от него нет.
   */
  lazy?: boolean
  /**
   * Запасной адрес, если основной не отдался.
   *
   * Нужен там, где кадр есть в двух местах и предпочтительное дешевле, но не
   * гарантировано: визуал карточки сайта лежит и в нашем томе (маршрут умеет
   * миниатюру), и на публичном хосте (полный кадр в полмегабайта). Просим
   * первое, а на промахе честно показываем второе — вместо погашенной плитки.
   */
  fallbackSrc?: string
  /**
   * Что показать, если картинка так и не загрузилась. По умолчанию — красная
   * «битая» плитка. Каталог Маркета подменяет её нейтральной заглушкой: там
   * мёртвая ссылка продавца — норма, копия на площадке при этом есть.
   */
  errorFallback?: ReactNode
  /** Размер окна увеличения, px. По умолчанию портрет 300x400 под макеты; квадратным картинкам — квадрат. */
  zoomSize?: { width: number; height: number }
  /**
   * Как картинка ложится в плитку. По умолчанию `cover`: макет A4 заполняет
   * портретную плитку. `contain` — для квадратных фигур (пряники): вписать
   * целиком, без обрезки по бокам.
   */
  fit?: 'cover' | 'contain'
}

/**
 * Portrait thumbnail with the same hover preview used in the pipeline table.
 * The enlarged image is rendered through a portal so table overflow cannot clip it.
 *
 * Картинка едет авторизованным запросом (см. useAuthorizedMediaUrl): маршруты
 * гейтвея закрыты, а `<img src>` заголовок Authorization не передаёт.
 */
export function ImagePreviewThumb({
  src: preferredSrc,
  alt = '',
  size = IMAGE_PREVIEW_THUMB_SIZE,
  thumbnailWidth,
  lazy = true,
  fallbackSrc,
  errorFallback,
  zoomSize,
  fit = 'cover',
}: ImagePreviewThumbProps) {
  const [failed, setFailed] = useState(false)
  const [fellBack, setFellBack] = useState(false)
  const [zoom, setZoom] = useState<{ top: number; left: number } | null>(null)
  const [zoomRequested, setZoomRequested] = useState(false)
  const ref = useRef<ElementRef<'img'>>(null)
  const { ref: viewportRef, near } = useNearViewport()
  const visible = !lazy || near
  const src = fellBack && fallbackSrc ? fallbackSrc : preferredSrc
  const media = useAuthorizedMediaUrl(visible ? thumbnailMediaUrl(src, thumbnailWidth) : undefined)

  // Смена адреса начинает историю заново: прежний промах к нему не относится.
  useEffect(() => {
    setFailed(false)
    setFellBack(false)
  }, [preferredSrc, fallbackSrc])

  // Основной адрес не отдался — переходим на запасной, если он есть.
  useEffect(() => {
    if (!fallbackSrc || fellBack) return
    if (media.error) setFellBack(true)
  }, [fallbackSrc, fellBack, media.error])

  // Плитка занимает 32 пикселя, окно увеличения — почти три сотни: один blob на
  // двоих означал бы либо мыло в окне, либо лишние сотни килобайт в списке.
  // Широкий вариант грузим только после первого наведения и держим до конца
  // жизни строки — повторное наведение открывается мгновенно.
  const needsWiderZoom = thumbnailWidth !== undefined && thumbnailWidth < ZOOM_MEDIA_WIDTH
  const zoomMedia = useAuthorizedMediaUrl(
    visible && zoomRequested && needsWiderZoom ? thumbnailMediaUrl(src, ZOOM_MEDIA_WIDTH) : undefined,
  )

  // Пока строка далеко — держим место скелетом: он же и есть наблюдаемый узел.
  if (!visible) {
    return (
      <span
        ref={viewportRef}
        role="status"
        aria-label="Загрузка изображения"
        className={`${size} shrink-0 rounded bg-gray-100 dark:bg-zinc-800`}
      />
    )
  }

  if (media.loading) {
    return (
      <span
        role="status"
        aria-label="Загрузка изображения"
        className={`${size} shrink-0 animate-pulse rounded bg-gray-100 dark:bg-zinc-800`}
      />
    )
  }
  // Пока переключаемся на запасной адрес, плитку не гасим: эффект выше уже
  // взвёл переход, и следующая отрисовка пойдёт по нему.
  if (media.error && fallbackSrc && !fellBack) {
    return (
      <span
        role="status"
        aria-label="Загрузка изображения"
        className={`${size} shrink-0 animate-pulse rounded bg-gray-100 dark:bg-zinc-800`}
      />
    )
  }
  // Ошибку показываем отдельно от «фото нет»: одинаковая серая плитка прятала бы
  // причину — файл недоступен, а не отсутствует.
  if (failed || media.error || !media.src) {
    return errorFallback !== undefined ? <>{errorFallback}</> : <ImagePreviewError size={size} alt={alt} />
  }

  const zoomWidth = zoomSize?.width ?? 300
  const zoomHeight = zoomSize?.height ?? 400
  const gap = 12

  const showZoom = () => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    setZoomRequested(true)

    let left = rect.right + gap
    if (left + zoomWidth > globalThis.innerWidth - 8) {
      left = rect.left - zoomWidth - gap
    }

    let top = rect.top + rect.height / 2 - zoomHeight / 2
    top = Math.max(8, Math.min(top, globalThis.innerHeight - zoomHeight - 8))
    setZoom({ top, left })
  }

  return (
    <>
      <img
        ref={ref}
        src={media.src}
        alt={alt}
        onError={() => {
          setZoom(null)
          // Битые байты основного адреса — тоже повод перейти на запасной.
          if (fallbackSrc && !fellBack) setFellBack(true)
          else setFailed(true)
        }}
        onMouseEnter={showZoom}
        onMouseLeave={() => setZoom(null)}
        className={`${size} shrink-0 rounded bg-gray-100 dark:bg-zinc-800 ${fit === 'contain' ? 'object-contain' : 'object-cover'}`}
      />
      {zoom &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: zoom.top,
              left: zoom.left,
              width: zoomWidth,
              height: zoomHeight,
            }}
            className="pointer-events-none z-[100] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
          >
            {/* Пока широкая версия едет — показываем плиточную: пустое окно на
                полсекунды заметнее, чем секунда мягкой картинки. */}
            <img
              src={zoomMedia.src ?? media.src}
              alt={alt}
              className="h-full w-full rounded object-contain"
            />
          </div>,
          globalThis.document.body,
        )}
    </>
  )
}

/** «Изображение не загрузилось» — видимая ошибка, а не пустая плитка. */
export function ImagePreviewError({
  size = IMAGE_PREVIEW_THUMB_SIZE,
  alt = '',
}: {
  size?: string
  alt?: string
}) {
  const label = alt ? `${alt}: ${MEDIA_ERROR_TITLE.toLowerCase()}` : MEDIA_ERROR_TITLE
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`${size} flex shrink-0 items-center justify-center rounded border border-dashed border-red-300 bg-red-50 text-red-400 dark:border-red-900 dark:bg-red-950/30 dark:text-red-500`}
    >
      <ImageOff className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  )
}

export function ImagePreviewPlaceholder({
  size = IMAGE_PREVIEW_THUMB_SIZE,
  gradient,
}: {
  size?: string
  gradient?: string
}) {
  return (
    <div
      aria-hidden="true"
      className={`${size} shrink-0 rounded ${
        gradient ? `bg-gradient-to-br ${gradient}` : 'bg-gray-200 dark:bg-zinc-700'
      }`}
    />
  )
}
